import React, { useState, useEffect } from 'react';
import { settingsAPI, rolePermissionsAPI, rolesAPI, accessControlAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FaCog, FaSave, FaUpload, FaTrash, FaEdit, FaUndo } from 'react-icons/fa';
import { useTheme, colorOptions } from '../context/ThemeContext';
import { useAppSettings } from '../context/AppSettingsContext';

// Logo, browser-tab icon and home-screen icon. Each upload is scaled down in the
// browser to `maxSize` px on its longest side before it is saved.
const BRANDING_ITEMS = [
  { key: 'report_primeclean_logo', title: 'App Logo', hint: 'Shown in the sidebar, on the login screen and on exported PDF/print headers.', maxSize: 512, empty: 'No logo' },
  { key: 'app_favicon', title: 'Favicon', hint: 'The browser tab icon. Square image recommended (e.g. 64×64 PNG).', maxSize: 128, empty: 'No favicon' },
  { key: 'app_icon', title: 'App Icon (Home Screen)', hint: 'Shown when a user adds the app to a tablet or phone home screen. Square PNG, 512×512 recommended.', maxSize: 512, empty: 'No app icon' },
];

const scaleImageToDataUrl = (file, maxSize) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read the file'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('That file is not a usable image'));
    image.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

// `section` renders one group of cards inside an App Configuration tab
// (general | masters | compliance | permissions | access | reports); none = all.
const AdminSettings = ({ section = null } = {}) => {
  const show = (s) => !section || section === s;
  const [settings, setSettings] = useState({});
  const [brandingBusy, setBrandingBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permMatrix, setPermMatrix] = useState({});
  const [permLabels, setPermLabels] = useState({});
  const [permLoading, setPermLoading] = useState(true);
  const [savingPermKey, setSavingPermKey] = useState(null);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [newRoleName, setNewRoleName] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [renamingRoleId, setRenamingRoleId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [acSettings, setAcSettings] = useState(null);
  const [acLoading, setAcLoading] = useState(true);
  const [acSaving, setAcSaving] = useState(false);
  const [acVisible, setAcVisible] = useState(true);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [newAddressLabel, setNewAddressLabel] = useState('');
  const [newAddressValue, setNewAddressValue] = useState('');
  const ROLE_OPTIONS = roles.filter((r) => r.is_active).map((r) => r.role_name);
  const { colorTheme, setColorTheme } = useTheme();
  const { appName, refreshAppSettings } = useAppSettings();
  useEffect(() => {
    loadSettings();
    loadRoles();
    loadAccessControl();
    loadDevices();
    rolePermissionsAPI.getAll()
      .then((res) => {
        setPermMatrix(res?.data?.data || {});
        setPermLabels(res?.data?.permission_keys || {});
      })
      .catch(() => toast.error('Failed to load role permissions.'))
      .finally(() => setPermLoading(false));
  }, []);

  const loadRoles = async () => {
    setRolesLoading(true);
    try {
      const res = await rolesAPI.getAll();
      setRoles(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      toast.error('Failed to load roles.');
    } finally {
      setRolesLoading(false);
    }
  };

  const createRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    setSavingRole(true);
    try {
      await rolesAPI.create(name);
      setNewRoleName('');
      await loadRoles();
      toast.success('Role created.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create role.');
    } finally {
      setSavingRole(false);
    }
  };

  const startRenameRole = (role) => {
    setRenamingRoleId(role.role_id);
    setRenameValue(role.role_name);
  };

  const saveRenameRole = async (roleId) => {
    const name = renameValue.trim();
    if (!name) return;
    setSavingRole(true);
    try {
      await rolesAPI.rename(roleId, name);
      setRenamingRoleId(null);
      await loadRoles();
      toast.success('Role renamed.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to rename role.');
    } finally {
      setSavingRole(false);
    }
  };

  const saveRoleLevel = async (roleId, level) => {
    try {
      await rolesAPI.setLevel(roleId, level === '' ? null : level);
      await loadRoles();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update level.');
    }
  };

  const deactivateRole = async (role) => {
    if (!window.confirm(`Deactivate the "${role.role_name}" role? Users must not currently hold it.`)) return;
    setSavingRole(true);
    try {
      await rolesAPI.deactivate(role.role_id);
      await loadRoles();
      toast.success('Role deactivated.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to deactivate role.');
    } finally {
      setSavingRole(false);
    }
  };

  // Adding an existing inactive role's name reactivates it server-side.
  const activateRole = async (role) => {
    setSavingRole(true);
    try {
      await rolesAPI.create(role.role_name);
      await loadRoles();
      toast.success('Role activated.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to activate role.');
    } finally {
      setSavingRole(false);
    }
  };

  const loadAccessControl = async () => {
    setAcLoading(true);
    try {
      const res = await accessControlAPI.getSettings();
      setAcSettings(res?.data?.data?.settings || null);
      setAcVisible(true);
    } catch (error) {
      setAcVisible(false); // not permitted (not Super Admin / IT Admin) — hide the card rather than error-toast
    } finally {
      setAcLoading(false);
    }
  };

  const loadDevices = async () => {
    setDevicesLoading(true);
    try {
      const res = await accessControlAPI.getDevices();
      setDevices(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (error) {
      // card already hides itself via acVisible when unauthorized
    } finally {
      setDevicesLoading(false);
    }
  };

  const saveAccessControl = async (next) => {
    setAcSaving(true);
    try {
      const res = await accessControlAPI.updateSettings(next);
      setAcSettings(res?.data?.data || next);
      toast.success('Access & Devices settings saved.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save settings.');
    } finally {
      setAcSaving(false);
    }
  };

  const toggleDeviceRule = (enabled) => saveAccessControl({ ...acSettings, devices: { ...acSettings.devices, enabled } });
  const setDeviceMode = (mode) => saveAccessControl({ ...acSettings, devices: { ...acSettings.devices, mode } });
  const setMaxDevices = (maxDevices) => setAcSettings((prev) => ({ ...prev, devices: { ...prev.devices, maxDevices } }));
  const toggleNetworkRule = (enabled) => saveAccessControl({ ...acSettings, network: { ...acSettings.network, enabled } });

  const addAllowedAddress = async () => {
    const address = newAddressValue.trim();
    if (!address) return;
    const next = { ...acSettings, network: { ...acSettings.network, allowed: [...acSettings.network.allowed, { label: newAddressLabel.trim(), address }] } };
    await saveAccessControl(next);
    setNewAddressLabel('');
    setNewAddressValue('');
  };

  const removeAllowedAddress = async (idx) => {
    const next = { ...acSettings, network: { ...acSettings.network, allowed: acSettings.network.allowed.filter((_, i) => i !== idx) } };
    await saveAccessControl(next);
  };

  const decideDevice = async (device, action) => {
    try {
      if (action === 'approve') await accessControlAPI.approveDevice(device.id);
      else if (action === 'revoke') await accessControlAPI.revokeDevice(device.id);
      else await accessControlAPI.forgetDevice(device.id);
      toast.success(action === 'approve' ? 'Device approved.' : action === 'revoke' ? 'Device blocked.' : 'Device removed.');
      loadDevices();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Action failed.');
    }
  };

  const isRoleAllowed = (permissionKey, role) => (permMatrix[permissionKey] || []).includes(role);

  const togglePermissionRole = async (permissionKey, role) => {
    const current = permMatrix[permissionKey] || [];
    const nextRoles = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    setSavingPermKey(permissionKey);
    try {
      const res = await rolePermissionsAPI.setRoles(permissionKey, nextRoles);
      setPermMatrix(res?.data?.data || permMatrix);
      toast.success('Role permission updated.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update role permission.');
    } finally {
      setSavingPermKey(null);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getAll();
      if (response.data && typeof response.data.data === 'object') {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: prev[key] === 'true' ? 'false' : 'true'
    }));
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleLogoUpload = (key, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      handleSettingChange(key, reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Branding saves straight away (no need to press Save Settings) so the sidebar
  // logo and the browser tab icon change as soon as the upload finishes.
  const saveBranding = async (item, value) => {
    setBrandingBusy(item.key);
    try {
      await settingsAPI.updateSetting(item.key, value);
      handleSettingChange(item.key, value);
      await refreshAppSettings();
      toast.success(value ? `${item.title} updated` : `${item.title} removed`);
    } catch (error) {
      toast.error(error.response?.data?.message || `Could not save the ${item.title.toLowerCase()}`);
    } finally {
      setBrandingBusy('');
    }
  };

  const handleBrandingUpload = async (item, file) => {
    if (!file) return;
    try {
      await saveBranding(item, await scaleImageToDataUrl(file, item.maxSize));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const keys = Object.keys(settings);
      for (const key of keys) {
        await settingsAPI.updateSetting(key, settings[key]);
      }
      await refreshAppSettings();
      toast.success('Settings saved successfully');
      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{textAlign: 'center', padding: '50px'}}><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div>
        {!section && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 className="page-title" style={{ margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}><FaCog /> System Settings</h2>
            <p className="page-subtitle">Configure API Master requirements and system behavior</p>
          </div>
        </div>
        )}

        <div style={{padding: section ? '0' : '30px', backgroundColor: section ? 'transparent' : 'var(--surface-light, #f9fafb)'}}>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px'}}>
            
            {/* API Master Settings Card */}
            {show('masters') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px'}}>
                API Master Data Entry
              </h3>

              {/* Solubility Requirement */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Solubility Classification (Mandatory)
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Users must enter solubility data
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_solubility_mandatory'] === 'true'}
                    onChange={() => handleToggle('api_master_solubility_mandatory')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* Cleanability Requirement */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Cleanability Rating (Mandatory)
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Users must enter cleanability data
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_cleanability_mandatory'] === 'true'}
                    onChange={() => handleToggle('api_master_cleanability_mandatory')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* HBEL Required */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      HBEL/PDE Value (Mandatory)
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Users must enter HBEL or PDE value
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_hbel_required'] === 'true'}
                    onChange={() => handleToggle('api_master_hbel_required')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* LD50 Required */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      LD50 Value (Mandatory)
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Users must enter LD50 value
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_ld50_required'] === 'true'}
                    onChange={() => handleToggle('api_master_ld50_required')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* Either HBEL or LD50 */}
              <div style={{marginBottom: '20px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Require Either HBEL or LD50
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      At least one of HBEL/PDE or LD50 must be entered
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_require_either_hbel_or_ld50'] === 'true'}
                    onChange={() => handleToggle('api_master_require_either_hbel_or_ld50')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>
            </div>
            </>)}

            {/* Product Master Settings Card */}
            {show('masters') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px'}}>
                Product Master
              </h3>
              <div style={{marginBottom: '20px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Show Color Used in Formulation
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Turn this off to hide the product color field when a client does not use it.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings['product_master_color_field_enabled'] !== 'false'}
                    onChange={() => handleToggle('product_master_color_field_enabled')}
                    style={{width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0}}
                  />
                </div>
              </div>
              <div style={{marginBottom: '20px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Show Coating Type and Coating Agent
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Turn this off to hide coating fields in Product Master. When enabled, Coating Agent appears after a coating type is selected, except for Uncoated.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings['product_master_coating_fields_enabled'] !== 'false'}
                    onChange={() => handleToggle('product_master_coating_fields_enabled')}
                    style={{width: '20px', height: '20px', cursor: 'pointer', flexShrink: 0}}
                  />
                </div>
              </div>
            </div>
            </>)}

            {/* Approval Settings Card */}
            {show('masters') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px'}}>
                Approval & Signature
              </h3>

              {/* Approval Required */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Approval Required
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      APIs must be approved before activation
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_approval_required'] === 'true'}
                    onChange={() => handleToggle('api_master_approval_required')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* Signature Required */}
              <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block'}}>
                      Electronic Signature Required
                    </label>
                    <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginTop: '4px'}}>
                      Electronic signature needed for approval
                    </p>
                  </div>
                  <input 
                    type="checkbox" 
                    checked={settings['api_master_signature_required'] === 'true'}
                    onChange={() => handleToggle('api_master_signature_required')}
                    style={{width: '20px', height: '20px', cursor: 'pointer'}}
                  />
                </div>
              </div>

              {/* Approval Role */}
              <div style={{marginBottom: '20px'}}>
                <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '8px'}}>
                  Approval Required By Role
                </label>
                <select 
                  value={settings['api_master_approval_required_role'] || 'Super Admin'}
                  onChange={(e) => setSettings(prev => ({...prev, 'api_master_approval_required_role': e.target.value}))}
                  style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--border-color)'}}
                >
                  <option>QA</option>
                  <option>Admin</option>
                  <option>Super Admin</option>
                </select>
              </div>
            </div>

            {/* Record approval levels moved to Settings → Approval Flow */}
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Record Approval Workflow
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '12px'}}>
                Whether each record type needs approval, how many levels, and which roles or users sign each level is now set in one place for every module: Product, API, Equipment, Train, Cleaning Config, Sampling Plan, Acceptance Criteria, Risk, CCV, Results, VRL, Training and more.
              </p>
              <a className="btn btn-primary" href="/settings?tab=approval">Open Approval Flow</a>
            </div>
            </>)}

            {/* Sampling Intimation Card */}
            {show('compliance') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Sampling Intimation
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '16px'}}>
                In-app notify-QC-before/after-cleaning workflow on Results, with both the send and the acknowledgement signed. Turn this off if this site tracks sampling intimation in separate software (e.g. a LIMS) instead.
              </p>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <label style={{fontWeight: '500', color: 'var(--text-primary)'}}>Enabled</label>
                <input
                  type="checkbox"
                  checked={settings['sampling_intimation_enabled'] !== 'false'}
                  onChange={() => handleSettingChange('sampling_intimation_enabled', settings['sampling_intimation_enabled'] !== 'false' ? 'false' : 'true')}
                  style={{width: '20px', height: '20px', cursor: 'pointer'}}
                />
              </div>
            </div>
            </>)}

            {/* Revalidation Policy Card */}
            {show('compliance') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Revalidation Policy
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '16px'}}>
                How many consecutive worst-case batches a revalidation study must cover, across every location, before it can be closed. Change this if your site's approved procedure requires a different number than 3.
              </p>
              <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Required batches</label>
              <input
                type="number"
                min="1"
                className="form-control"
                value={settings['revalidation_required_batches'] ?? ''}
                onChange={(e) => handleSettingChange('revalidation_required_batches', e.target.value)}
                style={{maxWidth: '160px'}}
              />
            </div>
            </>)}

            {/* Login & Password Policy Card */}
            {show('compliance') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Login &amp; Password Policy
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                21 CFR Part 11 access controls. Password complexity (8+ characters, upper/lower/number/special) is fixed; the thresholds below are editable. Save with the button at the top of this page.
              </p>
              {[
                { key: 'lockout_attempts', label: 'Failed attempts before lockout', hint: 'Account locks after this many consecutive wrong passwords.' },
                { key: 'lockout_minutes', label: 'Lockout duration (minutes)', hint: 'How long a locked account stays locked before it can retry.' },
                { key: 'password_expiry_days', label: 'Password expiry (days)', hint: 'Users must change their password after this many days.' },
                { key: 'password_history_count', label: 'Password history (reuse check)', hint: 'A new password cannot match any of this many previous passwords.' },
                { key: 'reauth_expire_minutes', label: 'Re-authentication validity (minutes)', hint: 'How long a critical-action re-auth (e.g. e-signature) stays valid.' },
              ].map(({ key, label, hint }, idx, arr) => (
                <div key={key} style={{marginBottom: idx === arr.length - 1 ? '0' : '14px'}}>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '2px'}}>{label}</label>
                  <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '0', marginBottom: '6px'}}>{hint}</p>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    value={settings[key] ?? ''}
                    onChange={(e) => handleSettingChange(key, e.target.value)}
                    style={{maxWidth: '160px'}}
                  />
                </div>
              ))}
            </div>
            </>)}

            {/* Role Master Card */}
            {show('permissions') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)', gridColumn: '1 / -1'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Roles
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                Create, rename or deactivate roles. "Super Admin" is a built-in role and cannot be renamed or deactivated. A role can only be deactivated once no active user holds it.
              </p>
              {rolesLoading ? (
                <div style={{ padding: '12px', color: 'var(--text-secondary)' }}>Loading…</div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {roles.map((role) => (
                      <div key={role.role_id} style={{
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px',
                        border: '1px solid var(--border-color)', opacity: role.is_active ? 1 : 0.5,
                      }}>
                        {renamingRoleId === role.role_id ? (
                          <>
                            <input
                              className="form-control"
                              style={{ width: '140px' }}
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              autoFocus
                            />
                            <button type="button" className="btn btn-sm btn-primary" disabled={savingRole} onClick={() => saveRenameRole(role.role_id)}>Save</button>
                            <button type="button" className="btn btn-sm btn-outline" onClick={() => setRenamingRoleId(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                              {role.role_name}{!role.is_active && ' (inactive)'}
                            </span>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              style={{ width: '56px', padding: '2px 6px' }}
                              title="Seniority level (higher = more senior); org-chart only, blank = unranked"
                              placeholder="Lvl"
                              defaultValue={role.level ?? ''}
                              onBlur={(e) => { const v = e.target.value; if (v !== String(role.level ?? '')) saveRoleLevel(role.role_id, v); }}
                            />
                            {!role.is_system && (
                              <>
                                <button type="button" className="btn-icon" title="Rename" onClick={() => startRenameRole(role)}><FaEdit /> </button>
                                {role.is_active ? (
                                  <button type="button" className="btn-icon" title="Deactivate" onClick={() => deactivateRole(role)}><FaTrash /></button>
                                ) : (
                                  <button type="button" className="btn-icon" title="Activate" disabled={savingRole} onClick={() => activateRole(role)}><FaUndo /></button>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', maxWidth: '360px' }}>
                    <input
                      className="form-control"
                      placeholder="New role name"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') createRole(); }}
                    />
                    <button type="button" className="btn btn-primary" disabled={savingRole || !newRoleName.trim()} onClick={createRole}>Add Role</button>
                  </div>
                </>
              )}
            </div>
            </>)}

            {/* Role Permissions Card */}
            {show('permissions') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)', gridColumn: '1 / -1'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Role Permissions
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                Which roles may perform each action. Super Admin can always perform every action regardless of these checkboxes — this cannot be disabled, so this screen can never lock everyone out of itself.
              </p>
              {permLoading ? (
                <div style={{ padding: '12px', color: 'var(--text-secondary)' }}>Loading…</div>
              ) : (
                <div className="table-container" style={{ overflowX: 'auto' }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Action</th>
                        {ROLE_OPTIONS.map((role) => (
                          <th key={role} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>{role}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(permLabels).map((permissionKey) => (
                        <tr key={permissionKey}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {permLabels[permissionKey]}
                            {savingPermKey === permissionKey && <span style={{ marginLeft: '8px', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Saving…</span>}
                          </td>
                          {ROLE_OPTIONS.map((role) => (
                            <td key={role} style={{ textAlign: 'center' }}>
                              {role === 'Super Admin' ? (
                                <input type="checkbox" checked disabled title="Super Admin always has access" />
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={isRoleAllowed(permissionKey, role)}
                                  disabled={savingPermKey === permissionKey}
                                  onChange={() => togglePermissionRole(permissionKey, role)}
                                />
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </>)}


            {/* Access & Devices Card — Super Admin / IT Admin only; hides itself for everyone else */}
            {show('access') && (<>
            {acVisible && (
              <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)', gridColumn: '1 / -1'}}>
                <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                  Access & Devices
                </h3>
                <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                  Restrict sign-in to the company network and/or require IT Admin approval for new devices. Super Admin and IT Admin are always exempt, so a mistake here can never lock the administrators out.
                </p>
                {acLoading || !acSettings ? (
                  <div style={{ padding: '12px', color: 'var(--text-secondary)' }}>Loading…</div>
                ) : (
                  <>
                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: acSettings.devices.enabled ? '12px' : 0 }}>
                        <strong style={{ color: 'var(--text-primary)' }}>Registered Devices</strong>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="checkbox" checked={acSettings.devices.enabled} disabled={acSaving} onChange={(e) => toggleDeviceRule(e.target.checked)} />
                          {acSettings.devices.enabled ? 'On' : 'Off'}
                        </label>
                      </div>
                      {acSettings.devices.enabled && (
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="radio" checked={acSettings.devices.mode === 'AUTO'} onChange={() => setDeviceMode('AUTO')} />
                            Auto-approve first N devices
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="radio" checked={acSettings.devices.mode === 'APPROVAL'} onChange={() => setDeviceMode('APPROVAL')} />
                            IT Admin approves every device
                          </label>
                          {acSettings.devices.mode === 'AUTO' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              Max devices per user:
                              <input type="number" min={1} max={20} className="form-control" style={{ width: '70px' }}
                                value={acSettings.devices.maxDevices}
                                onChange={(e) => setMaxDevices(Number(e.target.value))}
                                onBlur={() => saveAccessControl(acSettings)} />
                            </label>
                          )}
                          {acSettings.devices.mode === 'APPROVAL' && (
                            <button type="button" className="btn btn-sm btn-primary" disabled={acSaving} onClick={() => saveAccessControl(acSettings)}>Save</button>
                          )}
                        </div>
                      )}
                    </div>

                    <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: acSettings.network.enabled ? '12px' : 0 }}>
                        <strong style={{ color: 'var(--text-primary)' }}>Company Network Restriction</strong>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="checkbox" checked={acSettings.network.enabled} disabled={acSaving} onChange={(e) => toggleNetworkRule(e.target.checked)} />
                          {acSettings.network.enabled ? 'On' : 'Off'}
                        </label>
                      </div>
                      {acSettings.network.enabled && (
                        <>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                            {acSettings.network.allowed.map((entry, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                <span style={{ color: 'var(--text-primary)' }}>{entry.label ? `${entry.label}: ` : ''}{entry.address}</span>
                                <button type="button" className="btn-icon" title="Remove" onClick={() => removeAllowedAddress(idx)}><FaTrash /></button>
                              </div>
                            ))}
                            {!acSettings.network.allowed.length && <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}>No addresses allowed yet.</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', maxWidth: '480px' }}>
                            <input className="form-control" placeholder="Label (optional)" value={newAddressLabel} onChange={(e) => setNewAddressLabel(e.target.value)} style={{ width: '160px' }} />
                            <input className="form-control" placeholder="203.0.113.7 or 203.0.113.0/24 or 10.1.*.*" value={newAddressValue} onChange={(e) => setNewAddressValue(e.target.value)} />
                            <button type="button" className="btn btn-primary" disabled={acSaving || !newAddressValue.trim()} onClick={addAllowedAddress}>Add</button>
                          </div>
                        </>
                      )}
                    </div>

                    <div>
                      <strong style={{ color: 'var(--text-primary)', display: 'block', marginBottom: '10px' }}>Devices</strong>
                      {devicesLoading ? (
                        <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>
                      ) : (
                        <div className="table-container" style={{ overflowX: 'auto' }}>
                          <table>
                            <thead>
                              <tr>
                                <th>User</th><th>Device</th><th>Status</th><th>Last Seen</th><th>Last Address</th><th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devices.map((d) => (
                                <tr key={d.id}>
                                  <td>{d.name || d.username}</td>
                                  <td>{d.label || 'Unknown device'}</td>
                                  <td>{d.status}</td>
                                  <td>{d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleString() : '-'}</td>
                                  <td>{d.lastIp || '-'}</td>
                                  <td style={{ display: 'flex', gap: '6px' }}>
                                    {d.status !== 'APPROVED' && <button type="button" className="btn btn-sm btn-success-outline" onClick={() => decideDevice(d, 'approve')}>Approve</button>}
                                    {d.status !== 'REVOKED' && <button type="button" className="btn btn-sm btn-outline" onClick={() => decideDevice(d, 'revoke')}>Block</button>}
                                    <button type="button" className="btn btn-sm btn-outline" onClick={() => decideDevice(d, 'forget')}>Forget</button>
                                  </td>
                                </tr>
                              ))}
                              {!devices.length && <tr><td colSpan={6} style={{ color: 'var(--text-secondary)' }}>No devices recorded yet.</td></tr>}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            </>)}

            {/* Branding Card */}
            {show('general') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)', gridColumn: '1 / -1'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Branding
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                Uploads are applied immediately. Without a favicon or app icon, the logo is used in their place.
              </p>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px'}}>
                {BRANDING_ITEMS.map((item) => (
                  <div key={item.key} style={{border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column'}}>
                    <label style={{fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '4px'}}>
                      {item.key === 'report_primeclean_logo' ? `${appName} Logo` : item.title}
                    </label>
                    <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', margin: '0 0 10px', flex: '1 0 auto'}}>{item.hint}</p>
                    <div style={{height: '84px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', marginBottom: '10px', background: 'var(--bg-color)', border: settings[item.key] ? '1px solid var(--border-color)' : '1px dashed var(--border-color)'}}>
                      {settings[item.key]
                        ? <img src={settings[item.key]} alt={item.title} style={{maxHeight: '68px', maxWidth: '90%', objectFit: 'contain'}} />
                        : <span style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)'}}>{item.empty}</span>}
                    </div>
                    <div style={{display: 'flex', gap: '8px'}}>
                      <label className="btn btn-outline" style={{flex: 1, justifyContent: 'center', cursor: brandingBusy ? 'wait' : 'pointer', margin: 0}}>
                        <FaUpload /> {brandingBusy === item.key ? 'Saving…' : 'Upload'}
                        <input type="file" accept="image/*" hidden disabled={!!brandingBusy}
                          onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; handleBrandingUpload(item, file); }} />
                      </label>
                      {settings[item.key] && (
                        <button type="button" className="btn btn-outline" title={`Remove ${item.title.toLowerCase()}`} disabled={!!brandingBusy} onClick={() => saveBranding(item, '')}>
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </>)}

            {/* Report Branding Card */}
            {show('reports') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '20px'}}>
                PDF Header/Footer Branding
              </h3>

              <div style={{marginBottom: '14px'}}>
                <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Client Name</label>
                <input
                  className="form-control"
                  value={settings['report_client_name'] || ''}
                  onChange={(e) => handleSettingChange('report_client_name', e.target.value)}
                  placeholder="Client Name"
                />
              </div>

              <div style={{marginBottom: '14px'}}>
                <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Client Address</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={settings['report_client_address'] || ''}
                  onChange={(e) => handleSettingChange('report_client_address', e.target.value)}
                  placeholder="Client address for PDF header"
                />
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)', margin: '6px 0 0' }}>
                  Default only — while a unit is selected, its own address from Company &amp; Units is printed instead.
                </p>
              </div>

              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px'}}>
                <div>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Document Name</label>
                  <input
                    className="form-control"
                    value={settings['report_document_name'] || ''}
                    onChange={(e) => handleSettingChange('report_document_name', e.target.value)}
                    placeholder="Product Master Report"
                  />
                </div>
                <div>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Document Rev. No.</label>
                  <input
                    className="form-control"
                    value={settings['report_document_rev_no'] || ''}
                    onChange={(e) => handleSettingChange('report_document_rev_no', e.target.value)}
                    placeholder="01"
                  />
                </div>
              </div>

              <div style={{marginBottom: '14px'}}>
                <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Footer Tagline</label>
                <input
                  className="form-control"
                  value={settings['report_footer_tagline'] || ''}
                  onChange={(e) => handleSettingChange('report_footer_tagline', e.target.value)}
                  placeholder={`Data is generated and handled by ${appName}.`}
                />
              </div>

              <div style={{marginBottom: '14px', paddingTop: '10px', borderTop: '1px solid var(--border-color)'}}>
                <label style={{fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '4px'}}>Format Numbers (QA/SOP reference)</label>
                <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: '10px'}}>
                  Shown in each report's footer, e.g. <code>QA-SOP-011-F-01-00</code>. Leave blank to hide for that report.
                </p>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                  {[
                    { key: 'report_format_no_product_master', label: 'Product Master' },
                    { key: 'report_format_no_equipment_master', label: 'Equipment Master' },
                    { key: 'report_format_no_equipment_train', label: 'Equipment Train' },
                    { key: 'report_format_no_api_master', label: 'API Master' },
                    { key: 'report_format_no_maco', label: 'MACO Calculation' },
                    { key: 'report_format_no_dashboard', label: 'Dashboard Reports' }
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label style={{fontWeight: '500', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px', fontSize: 'var(--fs-sm)'}}>{label}</label>
                      <input
                        className="form-control"
                        value={settings[key] || ''}
                        onChange={(e) => handleSettingChange(key, e.target.value)}
                        placeholder="QA-SOP-011-F-01-00"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div style={{border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px'}}>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px'}}>Client Logo</label>
                  <input type="file" accept="image/*" onChange={(e) => handleLogoUpload('report_client_logo', e.target.files?.[0])} />
                  <input
                    className="form-control"
                    style={{marginTop: '8px'}}
                    value={settings['report_client_logo'] || ''}
                    onChange={(e) => handleSettingChange('report_client_logo', e.target.value)}
                    placeholder="Data URL or image URL"
                  />
                  {settings['report_client_logo'] && (
                    <img src={settings['report_client_logo']} alt="Client Logo" style={{marginTop: '8px', maxHeight: '46px', maxWidth: '100%'}} />
                  )}
                </div>
              </div>
            </div>
            </>)}

            {/* Location / Sample ID Numbering Card */}
            {show('reports') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Location &amp; Sample ID Numbering
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                Controls the prefix and zero-padding used when Sampling Plans auto-generates a new Plan Code, Location Code, or Sample ID. The running number itself increments automatically and is never reused.
              </p>

              <div style={{marginBottom: '18px', paddingBottom: '18px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px'}}>Plan Code</div>
                <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Prefix</label>
                    <input className="form-control" value={settings['numbering_plan_prefix'] || 'SP'} onChange={(e) => handleSettingChange('numbering_plan_prefix', e.target.value)} placeholder="SP" />
                  </div>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Digits</label>
                    <input className="form-control" type="number" min="1" max="8" value={settings['numbering_plan_padding'] || '4'} onChange={(e) => handleSettingChange('numbering_plan_padding', e.target.value)} />
                  </div>
                </div>
                <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '6px'}}>
                  Plan Codes are further suffixed by study type automatically, e.g. {(settings['numbering_plan_prefix'] || 'SP')}-{String(1).padStart(Number(settings['numbering_plan_padding'] || 4), '0')}-CV
                </div>
              </div>

              <div style={{marginBottom: '18px', paddingBottom: '18px', borderBottom: '1px solid var(--border-color)'}}>
                <div style={{fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px'}}>Location Code</div>
                <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Prefix</label>
                    <input className="form-control" value={settings['numbering_location_prefix'] || 'LOC'} onChange={(e) => handleSettingChange('numbering_location_prefix', e.target.value)} placeholder="LOC" />
                  </div>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Digits</label>
                    <input className="form-control" type="number" min="1" max="8" value={settings['numbering_location_padding'] || '3'} onChange={(e) => handleSettingChange('numbering_location_padding', e.target.value)} />
                  </div>
                </div>
                <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '6px'}}>
                  Example: {(settings['numbering_location_prefix'] || 'LOC')}-{String(1).padStart(Number(settings['numbering_location_padding'] || 3), '0')}
                </div>
              </div>

              <div>
                <div style={{fontWeight: '600', color: 'var(--text-primary)', marginBottom: '10px'}}>Sample ID</div>
                <div style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px'}}>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Prefix</label>
                    <input className="form-control" value={settings['numbering_sample_prefix'] || 'SMP'} onChange={(e) => handleSettingChange('numbering_sample_prefix', e.target.value)} placeholder="SMP" />
                  </div>
                  <div>
                    <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>Digits</label>
                    <input className="form-control" type="number" min="1" max="8" value={settings['numbering_sample_padding'] || '4'} onChange={(e) => handleSettingChange('numbering_sample_padding', e.target.value)} />
                  </div>
                </div>
                <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginTop: '6px'}}>
                  Sample IDs are further suffixed by sample type automatically, e.g. {(settings['numbering_sample_prefix'] || 'SMP')}-{String(1).padStart(Number(settings['numbering_sample_padding'] || 4), '0')}-SWB
                </div>
              </div>
            </div>
            </>)}

            {/* Visual Inspection Terminology Card */}
            {show('compliance') && (<>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                Visual Inspection Terminology
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '20px'}}>
                The wording shown on every Visual acceptance criterion (Acceptance Criteria and Sampling Plans pages) — some clients prefer "Acceptable / Not Acceptable" or "Clean / Not Clean" over "Pass / Fail". Only the label changes; the underlying stored value stays consistent.
              </p>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px'}}>
                <div>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>"Pass" label</label>
                  <input className="form-control" value={settings['visual_limit_pass_label'] || 'Pass'} onChange={(e) => handleSettingChange('visual_limit_pass_label', e.target.value)} placeholder="Pass" />
                </div>
                <div>
                  <label style={{fontWeight: '500', color: 'var(--text-primary)', display: 'block', marginBottom: '6px', fontSize: 'var(--fs-md)'}}>"Fail" label</label>
                  <input className="form-control" value={settings['visual_limit_fail_label'] || 'Fail'} onChange={(e) => handleSettingChange('visual_limit_fail_label', e.target.value)} placeholder="Fail" />
                </div>
              </div>
            </div>
            </>)}
          </div>

          {/* Appearance & Theme Card */}
          {show('general') && (<>
          <div style={{marginTop: '24px'}}>
            <div style={{background: 'var(--card-bg, white)', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid var(--border-color, #e5e7eb)'}}>
              <h3 style={{fontSize: 'var(--fs-xl)', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '6px'}}>
                🎨 Appearance &amp; Theme
              </h3>
              <p style={{fontSize: 'var(--fs-md)', color: 'var(--text-secondary)', marginBottom: '24px'}}>Customize the look and feel of {appName} across all pages.</p>

              <div>
                <label style={{fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '12px'}}>Color Theme</label>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '10px'}}>
                  {colorOptions.map(t => (
                    <button key={t.id} type="button" onClick={() => setColorTheme(t.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', background: colorTheme === t.id ? `${t.color}18` : 'var(--surface-light)', border: colorTheme === t.id ? `2px solid ${t.color}` : '2px solid var(--border-color)', boxShadow: colorTheme === t.id ? `0 4px 14px ${t.color}40` : 'none' }}
                    >
                      <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: t.color, flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }} />
                      <div>
                        <div style={{fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-primary)'}}>{t.label}</div>
                        <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>{t.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          </>)}

          {/* Save Button */}
          <div style={{marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '10px'}}>
            <button 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saving}
              style={{display: 'flex', alignItems: 'center', gap: '8px'}}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
